// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * GitHub, GitLab and Gitea/Forgejo read-only clients.
 *
 * Each was verified to answer `Access-Control-Allow-Origin: *` on the endpoints
 * used here, which is what makes a backend unnecessary.
 *
 * GitHub's *contents* API caps at 1 MB and would reject every board this tool
 * exists for, so file bodies come from `raw.githubusercontent.com` instead.
 * GitLab and Gitea both have raw endpoints without that limit.
 */

import {
  asRepoError,
  fromResponse,
  lfsPointer,
  notABoard,
  noBoards,
} from './errors.ts';
import {
  COMMIT_PAGE_SIZE,
  TREE_PAGE_SIZE,
  type FetchLike,
  type GitRef,
  type Provider,
  type ProviderId,
  type RepoInfo,
  type RepoSource,
} from './types.ts';

const LFS_MARKER = 'version https://git-lfs.github.com/spec/v1';

abstract class BaseProvider implements Provider {
  abstract readonly id: ProviderId;
  readonly source: RepoSource;
  protected readonly fetchImpl: FetchLike;

  constructor(source: RepoSource, fetchImpl: FetchLike) {
    this.source = source;
    this.fetchImpl = fetchImpl;
  }

  abstract info(): Promise<RepoInfo>;
  abstract listBoardFiles(ref: string): Promise<string[]>;
  abstract listRefs(): Promise<GitRef[]>;
  abstract listCommits(ref: string, path: string): Promise<GitRef[]>;
  abstract fetchFile(ref: string, path: string): Promise<string>;
  abstract webUrl(ref: string, path: string): string;

  protected get host(): string {
    return new URL(this.source.origin).hostname;
  }

  protected async getText(url: string, what: string): Promise<string> {
    let response;
    try {
      response = await this.fetchImpl(url, { headers: { Accept: 'application/json' } });
    } catch (err) {
      throw asRepoError(err, this.host, this.id);
    }
    if (!response.ok) throw fromResponse(what, this.host, response);
    return response.text();
  }

  protected async getJson<T>(url: string, what: string): Promise<T> {
    return (await this.getJsonWithHeaders<T>(url, what)).data;
  }

  protected async getJsonWithHeaders<T>(
    url: string,
    what: string,
  ): Promise<{ data: T; headers: { get(name: string): string | null } }> {
    let response;
    try {
      response = await this.fetchImpl(url, { headers: { Accept: 'application/json' } });
    } catch (err) {
      throw asRepoError(err, this.host, this.id);
    }
    if (!response.ok) throw fromResponse(what, this.host, response);
    const text = await response.text();
    try {
      return { data: JSON.parse(text) as T, headers: response.headers };
    } catch {
      throw notABoard(what, text);
    }
  }

  /** Shared checks on a downloaded file body. */
  protected validateBoard(path: string, body: string): string {
    if (body.startsWith(LFS_MARKER)) throw lfsPointer(path);
    const head = body.trimStart();
    if (!head.startsWith('(kicad_pcb')) throw notABoard(path, head);
    return body;
  }
}

// --- GitHub ---------------------------------------------------------------------

interface GhRepo {
  default_branch: string;
  full_name: string;
}
interface GhRef {
  name: string;
  commit?: { sha: string };
}
interface GhTree {
  truncated?: boolean;
  tree: Array<{ path: string; type: string }>;
}
interface GhCommit {
  sha: string;
  commit: { message: string; author?: { date?: string } };
}

export class GitHubProvider extends BaseProvider {
  readonly id = 'github' as const;

  private api(path: string): string {
    const base = this.host === 'github.com' ? 'https://api.github.com' : `${this.source.origin}/api/v3`;
    return `${base}/repos/${enc(this.source.owner)}/${enc(this.source.repo)}${path}`;
  }

  async info(): Promise<RepoInfo> {
    const repo = await this.getJson<GhRepo>(this.api(''), `repository ${this.source.label}`);
    return { defaultBranch: repo.default_branch, fullName: repo.full_name };
  }

  async listRefs(): Promise<GitRef[]> {
    const [branches, tags] = await Promise.all([
      this.getJson<GhRef[]>(this.api('/branches?per_page=100'), 'branch list'),
      this.getJson<GhRef[]>(this.api('/tags?per_page=100'), 'tag list').catch(() => [] as GhRef[]),
    ]);
    return [
      ...branches.map((b): GitRef => ({ kind: 'branch', id: b.name, label: b.name })),
      ...tags.map((t): GitRef => ({ kind: 'tag', id: t.name, label: t.name })),
    ];
  }

  async listBoardFiles(ref: string): Promise<string[]> {
    // GitHub returns the whole recursive tree in one response but truncates
    // very large ones, and says so. Ignoring that flag turns "we did not look
    // at everything" into "there is no board here".
    const tree = await this.getJson<GhTree>(
      this.api(`/git/trees/${enc(ref)}?recursive=1`),
      `file list for "${ref}"`,
    );
    const paths = tree.tree.filter((e) => e.type === 'blob').map((e) => e.path);
    return boardsFrom(paths, ref, tree.tree.length, tree.truncated === true);
  }

  async listCommits(ref: string, path: string): Promise<GitRef[]> {
    const commits = await this.getJson<GhCommit[]>(
      this.api(`/commits?sha=${enc(ref)}&path=${encodeURIComponent(path)}&per_page=${COMMIT_PAGE_SIZE}`),
      `commit history for "${path}"`,
    );
    return commits.map((c) => commitRef(c.sha, c.commit.message, c.commit.author?.date));
  }

  async fetchFile(ref: string, path: string): Promise<string> {
    // Not the contents API: it refuses anything over 1 MB.
    const base =
      this.host === 'github.com'
        ? 'https://raw.githubusercontent.com'
        : `${this.source.origin}/raw`;
    const url = `${base}/${enc(this.source.owner)}/${enc(this.source.repo)}/${enc(ref)}/${encodePath(path)}`;
    let response;
    try {
      response = await this.fetchImpl(url);
    } catch (err) {
      throw asRepoError(err, this.host, this.id);
    }
    if (!response.ok) throw fromResponse(`"${path}" at ${ref}`, this.host, response);
    return this.validateBoard(path, await response.text());
  }

  webUrl(ref: string, path: string): string {
    return `${this.source.origin}/${this.source.owner}/${this.source.repo}/blob/${enc(ref)}/${encodePath(path)}`;
  }
}

// --- GitLab ----------------------------------------------------------------------

interface GlProject {
  default_branch: string;
  path_with_namespace: string;
}
interface GlRef {
  name: string;
}
interface GlTreeEntry {
  path: string;
  type: string;
}
interface GlCommit {
  id: string;
  title: string;
  created_at?: string;
}

export class GitLabProvider extends BaseProvider {
  readonly id = 'gitlab' as const;

  private get projectId(): string {
    return encodeURIComponent(`${this.source.owner}/${this.source.repo}`);
  }

  private api(path: string): string {
    return `${this.source.origin}/api/v4/projects/${this.projectId}${path}`;
  }

  async info(): Promise<RepoInfo> {
    const project = await this.getJson<GlProject>(this.api(''), `repository ${this.source.label}`);
    return { defaultBranch: project.default_branch, fullName: project.path_with_namespace };
  }

  async listRefs(): Promise<GitRef[]> {
    const [branches, tags] = await Promise.all([
      this.getJson<GlRef[]>(this.api('/repository/branches?per_page=100'), 'branch list'),
      this.getJson<GlRef[]>(this.api('/repository/tags?per_page=100'), 'tag list').catch(() => [] as GlRef[]),
    ]);
    return [
      ...branches.map((b): GitRef => ({ kind: 'branch', id: b.name, label: b.name })),
      ...tags.map((t): GitRef => ({ kind: 'tag', id: t.name, label: t.name })),
    ];
  }

  async listBoardFiles(ref: string): Promise<string[]> {
    // GitLab paginates the tree. Reading only the first page silently misses
    // every board in a repository with more than a page of files.
    const paths: string[] = [];
    let page = 1;
    let scanned = 0;
    let truncated = false;
    for (;;) {
      const { data, headers } = await this.getJsonWithHeaders<GlTreeEntry[]>(
        this.api(`/repository/tree?ref=${enc(ref)}&recursive=true&per_page=${TREE_PAGE_SIZE}&page=${page}`),
        `file list for "${ref}"`,
      );
      scanned += data.length;
      for (const entry of data) if (entry.type === 'blob') paths.push(entry.path);

      const next = headers.get('x-next-page');
      if (next === null || next === '' || data.length === 0) break;
      page = Number(next);
      if (!Number.isFinite(page) || page > MAX_TREE_PAGES) {
        truncated = true;
        break;
      }
    }
    return boardsFrom(paths, ref, scanned, truncated);
  }

  async listCommits(ref: string, path: string): Promise<GitRef[]> {
    const commits = await this.getJson<GlCommit[]>(
      this.api(`/repository/commits?ref_name=${enc(ref)}&path=${encodeURIComponent(path)}&per_page=${COMMIT_PAGE_SIZE}`),
      `commit history for "${path}"`,
    );
    return commits.map((c) => commitRef(c.id, c.title, c.created_at));
  }

  async fetchFile(ref: string, path: string): Promise<string> {
    const url = this.api(`/repository/files/${encodeURIComponent(path)}/raw?ref=${enc(ref)}`);
    let response;
    try {
      response = await this.fetchImpl(url);
    } catch (err) {
      throw asRepoError(err, this.host, this.id);
    }
    if (!response.ok) throw fromResponse(`"${path}" at ${ref}`, this.host, response);
    return this.validateBoard(path, await response.text());
  }

  webUrl(ref: string, path: string): string {
    return `${this.source.origin}/${this.source.owner}/${this.source.repo}/-/blob/${enc(ref)}/${encodePath(path)}`;
  }
}

// --- Gitea / Forgejo (Codeberg) ----------------------------------------------------

interface GtRepo {
  default_branch: string;
  full_name: string;
}
interface GtRef {
  name: string;
}
interface GtTree {
  tree: Array<{ path: string; type: string }>;
  truncated?: boolean;
}
interface GtCommit {
  sha: string;
  commit: { message: string; author?: { date?: string } };
}

export class GiteaProvider extends BaseProvider {
  readonly id = 'gitea' as const;

  private api(path: string): string {
    return `${this.source.origin}/api/v1/repos/${enc(this.source.owner)}/${enc(this.source.repo)}${path}`;
  }

  async info(): Promise<RepoInfo> {
    const repo = await this.getJson<GtRepo>(this.api(''), `repository ${this.source.label}`);
    return { defaultBranch: repo.default_branch, fullName: repo.full_name };
  }

  async listRefs(): Promise<GitRef[]> {
    const [branches, tags] = await Promise.all([
      this.getJson<GtRef[]>(this.api('/branches?limit=100'), 'branch list'),
      this.getJson<GtRef[]>(this.api('/tags?limit=100'), 'tag list').catch(() => [] as GtRef[]),
    ]);
    return [
      ...branches.map((b): GitRef => ({ kind: 'branch', id: b.name, label: b.name })),
      ...tags.map((t): GitRef => ({ kind: 'tag', id: t.name, label: t.name })),
    ];
  }

  async listBoardFiles(ref: string): Promise<string[]> {
    const paths: string[] = [];
    let page = 1;
    let scanned = 0;
    let truncated = false;
    for (;;) {
      const tree = await this.getJson<GtTree>(
        this.api(`/git/trees/${enc(ref)}?recursive=true&per_page=${TREE_PAGE_SIZE}&page=${page}`),
        `file list for "${ref}"`,
      );
      const entries = tree.tree ?? [];
      scanned += entries.length;
      for (const entry of entries) if (entry.type === 'blob') paths.push(entry.path);
      if (tree.truncated === true) {
        truncated = true;
        break;
      }
      if (entries.length < TREE_PAGE_SIZE) break;
      page++;
      if (page > MAX_TREE_PAGES) {
        truncated = true;
        break;
      }
    }
    return boardsFrom(paths, ref, scanned, truncated);
  }

  async listCommits(ref: string, path: string): Promise<GitRef[]> {
    const commits = await this.getJson<GtCommit[]>(
      this.api(`/commits?sha=${enc(ref)}&path=${encodeURIComponent(path)}&limit=${COMMIT_PAGE_SIZE}`),
      `commit history for "${path}"`,
    );
    return commits.map((c) => commitRef(c.sha, c.commit.message, c.commit.author?.date));
  }

  async fetchFile(ref: string, path: string): Promise<string> {
    const url = this.api(`/raw/${encodePath(path)}?ref=${enc(ref)}`);
    let response;
    try {
      response = await this.fetchImpl(url);
    } catch (err) {
      throw asRepoError(err, this.host, this.id);
    }
    if (!response.ok) throw fromResponse(`"${path}" at ${ref}`, this.host, response);
    return this.validateBoard(path, await response.text());
  }

  webUrl(ref: string, path: string): string {
    return `${this.source.origin}/${this.source.owner}/${this.source.repo}/src/commit/${enc(ref)}/${encodePath(path)}`;
  }
}

// --- helpers -------------------------------------------------------------------------

function enc(segment: string): string {
  return segment.split('/').map(encodeURIComponent).join('/');
}

function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/**
 * Pages of tree entries to walk before giving up on a very large repository.
 *
 * GitLab returns directories before files, so a low cap can walk thousands of
 * entries without reaching a single blob — the KiCad source repository has
 * 21,000 entries and the first ten pages are all directories. At 1,000 entries
 * a page this covers repositories up to 40,000 entries, well past any board
 * project; anything larger reports honestly rather than claiming emptiness.
 */
const MAX_TREE_PAGES = 40;

function boardsFrom(paths: string[], ref: string, scanned: number, truncated: boolean): string[] {
  const boards = paths.filter((p) => p.toLowerCase().endsWith('.kicad_pcb')).sort();
  if (boards.length === 0) throw noBoards(ref, scanned, truncated);
  return boards;
}

function commitRef(sha: string, message: string, date?: string): GitRef {
  const subject = (message ?? '').split('\n')[0]!.trim();
  const when = date ? new Date(date) : undefined;
  const stamp = when && Number.isFinite(when.getTime()) ? when.toISOString().slice(0, 10) : '';
  return {
    kind: 'commit',
    id: sha,
    label: `${sha.slice(0, 8)}  ${subject.slice(0, 60)}`,
    detail: [stamp, subject].filter(Boolean).join('  '),
  };
}
